/**
 * The game's finish for rig figures: flat colour at the rig's resolution. Every piece is drawn in its
 * own colours, back to front, at art resolution and enlarged with Scale2x (which keeps pixel-art
 * edges clean); the full-resolution pieces (hands, fine face features) are then laid in finished,
 * and the weapon drawn at full resolution against the figure's depth (`layLate`).
 *
 * It keeps which layer put down each pixel, and how deep it lies, so the pieces drawn after the
 * enlargement go over what lies behind them and under what lies in front.
 */
import { PAD_TOP } from '../sprites';
import { atRes, canvas, ctx2d, selOutline, thinLines } from '../pixel';
import { FRAME, type Fit, type Layer, type Piece } from './figure';
import { BARE_ANCHORS } from './hair';
import type { Skeleton } from './skeleton';

/**
 * Buffers reused from one frame to the next: a crowd coming into view draws hundreds of frames,
 * and fresh buffers for each would keep the garbage collector (and the game) stalling.
 */
const POOL: {
  scratch?: CanvasRenderingContext2D;
  owner?: Int16Array;
  out2?: ImageData;
  owner2?: Int16Array;
  depth?: Float32Array;
  depth2?: Float32Array;
  lateZ?: Float32Array;
  lateMark?: Uint8Array;
} = {};

/**
 * One frame of a figure in the flat finish. At `res` 2 the figure is drawn at 1, enlarged with
 * Scale2x, and its fine pieces and weapon laid in at full double resolution.
 */
export function composeReal(layers: readonly Layer[], sk: Skeleton, res: 1 | 2 = 1): HTMLCanvasElement {
  let W = FRAME.w;
  let H = FRAME.h;
  let c = canvas(W, H);
  let g = ctx2d(c);
  const R = res;
  const sg = (POOL.scratch ??= ctx2d(canvas(W, H)));
  const scratch = sg.canvas;
  const fit: Fit = {
    anchors: layers.find((l) => l.anchors)?.anchors?.(sk.view) ?? BARE_ANCHORS,
    hidden: new Set(layers.flatMap((l) => l.hides ?? [])),
  };
  // Draw piece by piece, noting the layer of every pixel each one puts down.
  // At double resolution the weapon is drawn on its own afterwards, at full resolution.
  const late = (l: Layer) => R > 1 && l.id === 'weapon';
  const all = layers.flatMap((l, k) => (late(l) ? [] : l.pieces(sk, fit).map((p) => ({ p, k }))));
  // Pieces with a full-resolution drawing (hands, the face's features) lay their single-resolution
  // pixels first, and their fine drawing goes over them after the enlargement.
  const fine = R > 1 && all.some(({ p }) => p.fine);
  const pieces = [...all];
  pieces.sort((a, b) => a.p.z - b.p.z);
  let owner = (POOL.owner ??= new Int16Array(W * H)).fill(-1);
  // The depth of what was drawn at each pixel, so a piece laid in later at full resolution (a
  // hand) goes over what lies behind it and under what lies in front.
  let depth = (POOL.depth ??= new Float32Array(W * H)).fill(-Infinity);
  // Pieces one after another from the same layer are drawn together and read back once: whichever
  // of them is on top, the pixel belongs to that layer.
  for (let n = 0; n < pieces.length;) {
    const k = pieces[n]!.k;
    sg.setTransform(1, 0, 0, 1, 0, 0);
    sg.clearRect(0, 0, W, H);
    sg.translate(0, PAD_TOP);
    let z = -Infinity;
    for (; n < pieces.length && pieces[n]!.k === k; n++) {
      // A hand is read back on its own, so its pixels keep its own depth (see `Piece.hand`).
      if (pieces[n]!.p.hand && z > -Infinity) break;
      pieces[n]!.p.draw(sg);
      z = pieces[n]!.p.z;
      if (pieces[n]!.p.hand) {
        n++;
        break;
      }
    }
    sg.setTransform(1, 0, 0, 1, 0, 0);
    const d32 = new Uint32Array(sg.getImageData(0, 0, W, H).data.buffer);
    for (let i = 0; i < W * H; i++)
      // Alpha is the top byte of each little-endian pixel.
      if (d32[i]! >>> 24) {
        owner[i] = k;
        depth[i] = z;
      }
    g.drawImage(scratch, 0, 0);
  }
  if (R > 1) {
    // Scale2x: each pixel becomes four, each taking a neighbour's colour (and layer) where two
    // neighbours agree across that corner, so diagonal edges step by half pixels.
    const src = g.getImageData(0, 0, W, H);
    const px32 = new Uint32Array(src.data.buffer);
    const W2 = W * 2;
    const H2 = H * 2;
    // Every pixel of these is written below, so the pooled ones need no clearing.
    const out = (POOL.out2 ??= new ImageData(W2, H2));
    const o32 = new Uint32Array(out.data.buffer);
    const own2 = (POOL.owner2 ??= new Int16Array(W2 * H2));
    const dep2 = (POOL.depth2 ??= new Float32Array(W2 * H2));
    const copy = (j: number, from: number) => {
      o32[j] = px32[from]!;
      own2[j] = owner[from]!;
      dep2[j] = depth[from]!;
    };
    for (let y = 0; y < H; y++)
      for (let x = 0; x < W; x++) {
        const P = y * W + x;
        const A = y > 0 ? P - W : P;
        const B = x < W - 1 ? P + 1 : P;
        const C = x > 0 ? P - 1 : P;
        const D = y < H - 1 ? P + W : P;
        const a = px32[A];
        const b = px32[B];
        const cc = px32[C];
        const dd = px32[D];
        const j = y * 2 * W2 + x * 2;
        copy(j, cc === a && cc !== dd && a !== b ? A : P);
        copy(j + 1, a === b && a !== cc && b !== dd ? B : P);
        copy(j + W2, dd === cc && dd !== b && cc !== a ? C : P);
        copy(j + W2 + 1, b === dd && b !== a && dd !== cc ? D : P);
      }
    // The figure grid (density.ts): every one-art-pixel dark line made one G2 pixel, so the
    // enlarged parts draw their lines at the weight of the face, hands and weapon. (The 1x mode
    // brings the finished frame down from this, sets.ts.)
    thinLines(
      o32,
      W2,
      H2,
      2,
      own2,
      dep2,
      layers.findIndex((l) => l.id === 'face'),
    );
    W = W2;
    H = H2;
    owner = own2;
    depth = dep2;
    c = canvas(W, H);
    g = ctx2d(c);
    g.putImageData(out, 0, 0);
  }
  // Full-resolution pieces (hands, the face's fine features), drawn finished. They go over the body
  // and bare canvas, under hair, hats, beards and garments; the face's may stand out on its own.
  const fineMask = new Uint8Array(W * H);
  // The fingers of a hand closed round a weapon, laid back over it once it is drawn.
  const gripMask = new Uint8Array(W * H);
  let grips = false;
  if (fine) {
    const bodyK = layers.findIndex((l) => l.id === 'body');
    const bare = new Set(['body', 'face', 'hair', 'weapon', 'items', 'tool']);
    const clothed = layers.some((l) => !bare.has(l.id) && !l.id.startsWith('headwear:'));
    const cur = g.getImageData(0, 0, W, H);
    const c32 = new Uint32Array(cur.data.buffer);
    // Back to front, so a near hand lies over the far one.
    const fines = all.filter(({ p }) => p.fine).sort((x, y) => x.p.z - y.p.z);
    const fg = ctx2d(canvas(W, H));
    for (const { p, k } of fines) {
      fg.setTransform(1, 0, 0, 1, 0, 0);
      fg.clearRect(0, 0, W, H);
      fg.translate(0, PAD_TOP * R);
      p.fine!(fg, R);
      const f32 = new Uint32Array(fg.getImageData(0, 0, W, H).data.buffer);
      const loose = !clothed || layers[k]!.id === 'face';
      for (let i = 0; i < W * H; i++) {
        if (!(f32[i]! >>> 24)) continue;
        const o = owner[i]!;
        // Over another layer only where it lies behind (a ribbon's tails, a cape, the hair's tail
        // behind the head) — for the pieces that may stand out on their own (the face's).
        const behind = loose && depth[i]! < p.z;
        // A hand goes over whatever lies behind it (its own pixels included: depths are stored as
        // 32-bit floats), and under whatever lies in front.
        if (p.hand ? depth[i]! > p.z + 1e-4 : o >= 0 ? o !== bodyK && !fineMask[i] && !behind : !loose)
          continue;
        c32[i] = f32[i]!;
        owner[i] = bodyK;
        depth[i] = p.z;
        fineMask[i] = 1;
        if (p.grip) gripMask[i] = 1;
      }
      grips ||= !!p.grip;
    }
    g.putImageData(cur, 0, 0);
  }
  const done = c;
  if (R > 1) {
    // The weapon at full resolution, outlined on its own, laid in pixel by pixel against the depth
    // of the figure under it: the parts behind the body hidden by it, the parts in front over it.
    const fitR: Fit = { ...fit, res: R };
    const dg = ctx2d(done);
    const held = grips ? dg.getImageData(0, 0, W, H) : undefined;
    let wg: CanvasRenderingContext2D | undefined;
    const latePieces = layers.filter(late).flatMap((l) => l.pieces(sk, fitR));
    if (latePieces.length) layLate(dg, latePieces, depth, owner, (wg ??= ctx2d(canvas(W, H))), W, H, R);
    if (held && wg) {
      // The gripping fingers close over the grip.
      const now = dg.getImageData(0, 0, W, H);
      const n32 = new Uint32Array(now.data.buffer);
      const h32 = new Uint32Array(held.data.buffer);
      for (let i = 0; i < W * H; i++) if (gripMask[i]) n32[i] = h32[i]!;
      dg.putImageData(now, 0, 0);
    }
  }
  return atRes(done, R);
}

/**
 * Lays the pieces drawn at full resolution (the weapon, its scabbard) into the finished figure on
 * `dg`, each pixel only where it lies nearer the camera than what is already there: a blade held
 * beside a figure seen from behind shows past its outline but goes behind its back, and a strike
 * passes in front of the body and behind it as its depth (`Piece.depth`) runs along the blade.
 * Each is outlined on its own first; the figure's outline counts as the depth of the body it rings.
 * Whatever is laid over the figure casts a one-pixel contact shadow onto it, away from the light.
 */
function layLate(
  dg: CanvasRenderingContext2D,
  pieces: readonly Piece[],
  depth: Float32Array,
  owner: Int16Array,
  wg: CanvasRenderingContext2D,
  W: number,
  H: number,
  R: number,
): void {
  const cur = dg.getImageData(0, 0, W, H);
  const c32 = new Uint32Array(cur.data.buffer);
  const N = W * H;
  const lz = (POOL.lateZ = POOL.lateZ?.length === N ? POOL.lateZ : new Float32Array(N));
  const mark = (POOL.lateMark = POOL.lateMark?.length === N ? POOL.lateMark : new Uint8Array(N));
  mark.fill(0);
  const body = (i: number) => owner[i]! >= 0 && Number.isFinite(depth[i]!);
  for (let i = 0; i < N; i++) {
    if (!(c32[i]! >>> 24)) lz[i] = -Infinity;
    else if (body(i)) lz[i] = depth[i]!;
    else {
      // The outline: as deep as the nearest of the body it rings.
      let z = -Infinity;
      const x = i % W;
      if (x > 0 && body(i - 1)) z = Math.max(z, depth[i - 1]!);
      if (x < W - 1 && body(i + 1)) z = Math.max(z, depth[i + 1]!);
      if (i >= W && body(i - W)) z = Math.max(z, depth[i - W]!);
      if (i < N - W && body(i + W)) z = Math.max(z, depth[i + W]!);
      lz[i] = z;
    }
  }
  const top = PAD_TOP * R;
  // Back to front, so a hilt goes over its own scabbard.
  for (const p of [...pieces].sort((a, b) => a.z - b.z)) {
    wg.setTransform(1, 0, 0, 1, 0, 0);
    wg.clearRect(0, 0, W, H);
    wg.translate(0, top);
    p.draw(wg);
    const o32 = new Uint32Array(ctx2d(selOutline(wg.canvas)).getImageData(0, 0, W, H).data.buffer);
    for (let i = 0; i < N; i++) {
      if (!(o32[i]! >>> 24)) continue;
      const z = p.depth ? p.depth((i % W) + 0.5, Math.floor(i / W) + 0.5 - top) : p.z;
      if (z < lz[i]!) continue;
      c32[i] = o32[i]!;
      // Over the figure (not over bare canvas): casts a contact shadow.
      mark[i] = Number.isFinite(lz[i]!) ? 2 : 1;
      lz[i] = z;
    }
  }
  // Contact shadows down and to the left of what lies over the figure, on the figure's own pixels.
  const d8 = cur.data;
  for (let i = 0; i < N; i++) {
    if (mark[i] !== 2) continue;
    const x = i % W;
    if (x < R) continue;
    for (const dy of [R, 2 * R]) {
      const t = i + dy * W - R;
      if (t >= N || mark[t] || owner[t]! < 0) continue;
      mark[t] = 3;
      const q = t * 4;
      const k = dy === R ? 0.7 : 0.82;
      d8[q] = d8[q]! * k + 44 * (1 - k) * 0.45;
      d8[q + 1] = d8[q + 1]! * k + 40 * (1 - k) * 0.45;
      d8[q + 2] = d8[q + 2]! * k + 96 * (1 - k) * 0.45;
    }
  }
  dg.putImageData(cur, 0, 0);
}
